import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useCodeLabel, useI18n, useLocalized } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatDateTime, formatGel, formatPoints } from '../lib/format';
import { useInspectionRules, useOrder, useOrderInspections, useRefundOrder } from '../lib/queries';
import { can, useSession } from '../lib/session';
import { fromOrderDetail } from '../components/Invoice';
import { PrintableInvoice } from '../components/PrintableInvoice';
import { StaffPayEditor } from '../components/StaffPayEditor';
import { InspectionGallery } from '../components/inspection/InspectionGallery';
import { PhotosSheet } from '../components/inspection/FloorInspection';
import { TakePayment } from '../components/TakePayment';
import { EraseButton } from '../components/EraseButton';
import { OrderEraseEffects } from '../components/OrderEraseEffects';
import { Deadline } from '../components/Deadline';
import type { RefundResult, WorkStatus } from '../lib/types';
import {
  Alert, Amount, Button, Card, Field, Pill, SectionTitle, Skeleton, Stepper, TierBadge,
} from '../components/ui';

const WORK_TONE: Record<WorkStatus, 'neutral' | 'warning' | 'positive'> = {
  PENDING: 'neutral', IN_PROGRESS: 'warning', DONE: 'positive',
};

function RefundPanel({
  orderId, items, onDone,
}: {
  orderId: string;
  items: Array<{ id: string; name: string; refundable: number; unitPriceTetri: number }>;
  onDone: (result: RefundResult) => void;
}) {
  const { t } = useI18n();
  const refund = useRefundOrder(orderId);
  const [reason, setReason] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [confirming, setConfirming] = useState(false);

  const selected = items
    .map((item) => ({ item, quantity: quantities[item.id] ?? 0 }))
    .filter((entry) => entry.quantity > 0);

  const grossTetri = selected.reduce((t, e) => t + e.item.unitPriceTetri * e.quantity, 0);
  const isEverything = items.every((item) => (quantities[item.id] ?? 0) === item.refundable);
  const errorText = useApiError();
  const error = refund.error instanceof ApiError ? refund.error : null;

  return (
    <Card>
      <SectionTitle>{t('order.refund')}</SectionTitle>

      {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-raised/40 px-3 py-2.5">
            <div className="min-w-0">
              <div className="truncate text-sm text-ink">{item.name}</div>
              <div className="tabular text-[0.7rem] text-ink-faint">
                {formatGel(item.unitPriceTetri)} · {t('order.refundable', { count: item.refundable })}
              </div>
            </div>
            <Stepper
              value={quantities[item.id] ?? 0}
              min={0}
              max={item.refundable}
              onChange={(next) => setQuantities((prev) => ({ ...prev, [item.id]: next }))}
            />
          </div>
        ))}
      </div>

      <button
        onClick={() => setQuantities(Object.fromEntries(items.map((i) => [i.id, i.refundable])))}
        className="mt-3 inline-flex min-h-11 items-center text-xs text-brand transition hover:underline sm:min-h-8"
      >
        {t('order.selectAllRefundable')}
      </button>

      <div className="mt-4">
        <Field
          label={t('common.reason')}
          placeholder={t('order.refundReason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint={t('order.reasonAudited')}
        />
      </div>

      {!confirming ? (
        <Button
          variant="danger"
          className="mt-4 w-full"
          disabled={selected.length === 0 || reason.trim().length < 3}
          onClick={() => setConfirming(true)}
        >
          {t('order.refundAmount', { amount: formatGel(grossTetri) })}
        </Button>
      ) : (
        <div className="mt-4 rounded-xl border border-danger/35 bg-danger/10 p-4">
          <p className="text-sm font-semibold text-ink">
            {isEverything ? t('order.confirmFull') : t('order.confirmPartial')}
          </p>
          <ul className="mt-2 space-y-1 text-xs text-ink-dim">
            <li>· {t('order.refundBullet1', { amount: formatGel(grossTetri) })}</li>
            <li>· {t('order.refundBullet2')}</li>
            <li>· {t('order.refundBullet3')}</li>
            <li>· {t('order.refundBullet4')}</li>
            <li>· {t('order.refundBullet5')}</li>
          </ul>
          <div className="mt-4 flex gap-2">
            <Button
              variant="danger"
              className="flex-1"
              loading={refund.isPending}
              onClick={() => {
                refund.mutate({
                  reason: reason.trim(),
                  items: selected.map((e) => ({ orderItemId: e.item.id, quantity: e.quantity })),
                }, { onSuccess: onDone });
              }}
            >
              {t('order.confirmRefund')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function OrderPage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const code = useCodeLabel();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { data, isPending, error } = useOrder(id);
  const { data: inspections } = useOrderInspections(id);
  const [photosFor, setPhotosFor] = useState<'INTAKE' | 'HANDOVER' | null>(null);
  const [result, setResult] = useState<RefundResult | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const { data: rules } = useInspectionRules();

  const refundable = useMemo(
    () => (data?.items ?? [])
      .map((item) => ({
        id: item.id,
        name: localized(item.serviceNameSnapshot),
        refundable: item.quantity - item.refundedQuantity,
        unitPriceTetri: item.unitPriceTetri,
      }))
      .filter((item) => item.refundable > 0),
    [data],
  );

  if (isPending) return <div className="mx-auto max-w-3xl space-y-3"><Skeleton className="h-28" /><Skeleton className="h-48" /></div>;
  if (error || !data) return <div className="mx-auto max-w-2xl"><Alert>{t('order.couldNotLoad')}</Alert></div>;

  const { order, items, payments, refunds } = data;
  const taken = new Set((inspections?.items ?? []).map((inspection) => inspection.kind));
  // Only a car taken in on the floor is photographed in and out; a sale at the counter never was.
  const missingPhotos = order.acceptedAt && order.vehicleId && order.status !== 'CANCELLED' && can(session, 'orders.accept')
    ? (['INTAKE', 'HANDOVER'] as const).filter((kind) => !taken.has(kind))
    : [];
  const mayRefund = can(session, 'orders.refund');
  const maySeePay = can(session, 'staff.pay');
  // A car still on the floor: its work and deadline matter here, money not yet.
  const onFloor = ['CONFIRMED', 'IN_PROGRESS', 'READY'].includes(order.status);
  const canRefund = !onFloor && mayRefund && refundable.length > 0 && order.status !== 'CANCELLED';
  const till = `/orders/new?customer=${order.customerId}&accepted=${order.id}`;
  const handOver = () => {
    // Photographed before it leaves, when the studio requires it, exactly as from the floor.
    if (!rules?.handoverRequired || taken.has('HANDOVER') || !order.vehicleId) navigate(till);
    else if (can(session, 'orders.accept')) setHandingOver(true);
    else navigate(`${till}&handoverLater=1`);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/orders" className="-my-2 inline-flex min-h-11 items-center text-xs text-ink-faint transition hover:text-ink-dim">{t('order.back')}</Link>

      <header className="mb-5 mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="tabular font-display text-[1.75rem] font-bold tracking-tight text-ink sm:text-[2rem]">
            {order.reference}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-ink-dim">
            <Link to={`/customers/${order.customerId}`} className="font-medium text-ink hover:text-brand">
              {order.customerName}
            </Link>
            {order.tierCodeApplied && <TierBadge code={order.tierCodeApplied} size="sm" />}
            <span className="text-ink-faint">
              {order.completedAt
                ? formatDateTime(order.completedAt)
                : onFloor && order.acceptedAt
                  ? t('order.acceptedAt', { time: formatDateTime(order.acceptedAt) })
                  : t('order.notCompleted')}
            </span>
          </div>
          {onFloor && (
            <div className="mt-1">
              <Deadline orderId={order.id} promisedAt={order.promisedAt} mayEdit={can(session, 'orders.accept')} />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {order.status !== 'COMPLETED' && (
            <Pill tone={order.status === 'CANCELLED' ? 'danger' : 'warning'}>
              {code('status', order.status)}
            </Pill>
          )}
          {order.completedAt && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // The sheet has to be in the document for the print rules to
                // reach it, so it is mounted first and printed on the next frame.
                setShowReceipt(true);
                requestAnimationFrame(() => window.print());
              }}
            >
              {t('order.printReceipt')}
            </Button>
          )}
          <EraseButton
            compact
            path={`orders/${order.id}`}
            name={order.reference}
            details={(open) => <OrderEraseEffects orderId={order.id} open={open} />}
            onDone={() => navigate('/orders', { replace: true })}
          />
        </div>
      </header>

      {onFloor && (
        <div className="mb-4 flex flex-wrap gap-2">
          {can(session, 'orders.complete') && (
            <Button className="flex-1 sm:flex-none" variant={order.status === 'READY' ? 'primary' : 'ghost'} onClick={handOver}>
              {t('floor.handOver')}
            </Button>
          )}
          <Button className="flex-1 sm:flex-none" variant="quiet" onClick={() => navigate('/floor')}>{t('calendar.toFloor')}</Button>
        </div>
      )}

      {handingOver && (
        <PhotosSheet
          orderId={order.id}
          kind="HANDOVER"
          thenHandOver
          onClose={() => setHandingOver(false)}
          onDone={() => { setHandingOver(false); navigate(till); }}
          onLater={() => navigate(`${till}&handoverLater=1`)}
        />
      )}

      {!onFloor && can(session, 'orders.complete') && (
        <div className="mb-4">
          <TakePayment
            orderId={data.order.id}
            payableTetri={data.order.amountPayableTetri}
            paidTetri={data.order.paidAmountTetri}
          />
        </div>
      )}

      {photosFor && order.vehicleId && (
        <PhotosSheet
          orderId={order.id}
          kind={photosFor}
          vehicle={{ id: order.vehicleId, customerId: order.customerId, vin: order.vehicleVin }}
          onClose={() => setPhotosFor(null)}
          onDone={() => setPhotosFor(null)}
        />
      )}

      {showReceipt && (
        <div className="mb-4">
          <div className="mb-2 flex justify-end">
            <Button variant="quiet" size="sm" onClick={() => setShowReceipt(false)}>
              {t('order.hideReceipt')}
            </Button>
          </div>
          <PrintableInvoice data={fromOrderDetail(data)} />
        </div>
      )}

      {result && (
        <div className="mb-4">
          <Alert tone="info">
            <p className="font-semibold text-ink">
              {t('order.refundedTitle', { amount: formatGel(result.refundValueTetri) })}
              {result.isFullRefund && t('order.wholeOrder')}
            </p>
            <ul className="mt-2 space-y-0.5 text-xs text-ink-dim">
              <li>{t('order.cashToHandBack', { amount: formatGel(result.cashRefundTetri) })}</li>
              {result.cashbackReturnedTetri > 0 && (
                <li>{t('order.cashbackReturned', { amount: formatGel(result.cashbackReturnedTetri) })}</li>
              )}
              <li>{t('order.cashbackReversed', { amount: formatGel(result.cashbackReversedTetri) })}</li>
              <li>{t('order.pointsReversed', { points: formatPoints(result.pointsReversed) })}</li>
              {result.cashbackForgivenTetri !== 0 && (
                <li>{t('order.forgiven', { amount: formatGel(result.cashbackForgivenTetri) })}</li>
              )}
              <li>{t('order.tierNow', { tier: result.tierCode })}</li>
            </ul>
          </Alert>
        </div>
      )}

      <div className={`grid gap-4 lg:items-start ${onFloor ? '' : 'lg:grid-cols-[minmax(0,1fr)_20rem]'}`}>
        <div className="min-w-0 space-y-4">
          <Card>
            <SectionTitle>{t('order.services')}</SectionTitle>
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.id} className="flex items-baseline justify-between gap-3 border-b border-line/60 pb-2 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm text-ink">{localized(item.serviceNameSnapshot)}</span>
                      {onFloor && <Pill tone={WORK_TONE[item.workStatus]}>{t(`work.status.${item.workStatus}`)}</Pill>}
                    </div>
                    {/* Prices are set at the till; until then there is only the work. */}
                    {!onFloor && <div className="tabular text-[0.7rem] text-ink-faint">
                      {item.quantity} × {formatGel(item.unitPriceTetri)}
                      {item.refundedQuantity > 0
                        && ` · ${t('order.refundedQty', { count: item.refundedQuantity })}`}
                    </div>}
                    {item.performedByName && (
                      <div className="mt-0.5 text-[0.7rem] text-ink-dim">
                        {t(onFloor ? 'order.on' : 'order.by', { name: item.performedByName })}
                        {item.performedByJobTitle && (
                          <span className="text-ink-faint"> · {item.performedByJobTitle}</span>
                        )}
                      </div>
                    )}
                    {maySeePay && item.performedByAdminId && order.status !== 'CANCELLED' && (
                      <StaffPayEditor orderId={order.id} item={item} />
                    )}
                  </div>
                  {!onFloor && <Amount value={formatGel(item.lineTotalTetri)} className="text-sm text-ink" />}
                </div>
              ))}
            </div>
          </Card>

          {((inspections?.items.length ?? 0) > 0 || order.vehicleId) && (
            <Card>
              <SectionTitle action={order.vehicleId && (
                <Link to={`/vehicles/${order.vehicleId}`} className="text-xs font-medium text-brand hover:underline">
                  {t('vehicle.history')} →
                </Link>
              )}>{t('inspection.orderSection')}</SectionTitle>
              {(inspections?.items.length ?? 0) === 0
                ? <p className="text-sm text-ink-dim">{t('inspection.notTaken')}</p>
                : (
                  <div className="space-y-5">
                    {inspections!.items.map((inspection) => (
                      <InspectionGallery key={inspection.id} inspection={inspection} />
                    ))}
                  </div>
                )}
              {/* A car taken in and handed back without its photos is photographed afterwards from here. */}
              {missingPhotos.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {missingPhotos.map((kind) => (
                    <Button key={kind} variant="ghost" size="sm" onClick={() => setPhotosFor(kind)}>
                      {kind === 'INTAKE' ? t('inspection.addIntake') : t('inspection.addHandover')}
                    </Button>
                  ))}
                </div>
              )}
            </Card>
          )}

          {order.notes && (
            <Card>
              <SectionTitle>{t('order.note')}</SectionTitle>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-dim">{order.notes}</p>
            </Card>
          )}

          {(!onFloor || payments.length > 0) && <Card>
            <SectionTitle>{t('order.payments')}</SectionTitle>
            <div className="space-y-1.5">
              {payments.map((payment) => (
                <div key={payment.id} className="flex justify-between gap-3 text-sm">
                  <span className="text-ink-dim">{code('method', payment.method)}</span>
                  <Amount
                    value={formatGel(payment.amountTetri)}
                    className={payment.method === 'CASHBACK' ? 'text-brand' : 'text-ink'}
                  />
                </div>
              ))}
            </div>
          </Card>}

          {refunds.length > 0 && (
            <Card>
              <SectionTitle>{t('order.refundHistory')}</SectionTitle>
              <div className="space-y-3">
                {refunds.map((entry) => (
                  <div key={entry.id} className="border-b border-line/60 pb-3 last:border-0 last:pb-0">
                    <div className="flex justify-between gap-3">
                      <span className="text-sm text-ink">{formatGel(entry.refundValueTetri)}</span>
                      <span className="text-[0.7rem] text-ink-faint">{formatDateTime(entry.createdAt)}</span>
                    </div>
                    <p className="mt-1 text-xs text-ink-dim">{entry.reason}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {canRefund && <RefundPanel orderId={order.id} items={refundable} onDone={setResult} />}
          {!onFloor && mayRefund && refundable.length === 0 && (
            <Card><p className="text-sm text-ink-dim">{t('order.fullyRefunded')}</p></Card>
          )}
        </div>

        {!onFloor && <aside className="min-w-0 lg:sticky lg:top-6">
          <Card>
            <SectionTitle>{t('order.totals')}</SectionTitle>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">{t('order.subtotal')}</dt>
                <dd><Amount value={formatGel(order.subtotalTetri)} className="text-ink" /></dd>
              </div>
              {order.discountTetri > 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-dim">{t('order.discount', { percent: order.discountBp / 100 })}</dt>
                  <dd className="tabular text-ink">−{formatGel(order.discountTetri)}</dd>
                </div>
              )}
              {order.cashbackUsedTetri > 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-dim">{t('order.cashbackApplied')}</dt>
                  <dd className="tabular text-brand">−{formatGel(order.cashbackUsedTetri)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-3 border-t border-line pt-2">
                <dt className="text-ink-dim">{t('order.value')}</dt>
                <dd><Amount value={formatGel(order.orderValueTetri)} className="font-semibold text-ink" /></dd>
              </div>
              {order.refundedValueTetri > 0 && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-dim">{t('order.refunded')}</dt>
                  <dd className="tabular text-warning">−{formatGel(order.refundedValueTetri)}</dd>
                </div>
              )}
              {maySeePay && order.staffPayOwedTetri !== null && order.staffPayOwedTetri > 0 && (
                <div className="flex justify-between gap-3 border-t border-line pt-2">
                  <dt className="text-ink-dim">{t('pay.orderTotal')}</dt>
                  <dd><Amount value={formatGel(order.staffPayOwedTetri)} className="text-ink" /></dd>
                </div>
              )}
            </dl>

            <dl className="mt-4 space-y-1.5 border-t border-line pt-3 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-faint">{t('order.cashbackEarned')}</dt>
                <dd className="tabular text-positive">+{formatGel(order.cashbackEarnedTetri)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-faint">{t('order.pointsEarned')}</dt>
                <dd className="tabular text-positive">+{formatPoints(order.pointsEarned)}</dd>
              </div>
              {order.rulesVersion !== null && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-faint">{t('order.rulesVersion')}</dt>
                  <dd className="tabular text-ink-dim">v{order.rulesVersion}</dd>
                </div>
              )}
            </dl>
          </Card>
        </aside>}
      </div>
    </div>
  );
}
